"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { fitWindow, LinesGraph } from "@/components/tools/algebra/lines-graph";
import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { checkAt, solveLinearText } from "@/lib/tool-kit/algebra/linear";
import type { RelationStep } from "@/lib/tool-kit/algebra/relation";
import {
  formatUni,
  substitutionText,
  type Uni,
  uniEval,
  variableText,
} from "@/lib/tool-kit/algebra/uni";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  absR,
  formatStyled,
  type NumberStyle,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const LINEAR_EQUATION_SOLVER_STRINGS = {
  equation: "Equation",
  equationHint:
    "One letter for the unknown. Brackets, fractions (x/3) and decimals are fine, for example 3(x − 2) + 4 = 2x + 7.",
  examplesLabel: "Try an example",
  examples: [
    "3(x − 2) + 4 = 2x + 7",
    "x/3 + 1/2 = 5",
    "5 − 2x = 11",
    "2x + 3 = 2x + 5",
    "2(x + 1) = 2x + 2",
  ],
  results: "Solution",
  answer: "Answer",
  decimal: "As a decimal",
  type: "Type of equation",
  kinds: {
    one: "One solution",
    none: "No solution",
    all: "Infinitely many solutions",
  },
  copy: "Copy solution",
  copied: "Copied!",
  stepsHeading: "Step by step",
  start: "Start with the equation.",
  simplify: "Simplify each side: expand the brackets and combine like terms.",
  clearFractions: "Multiply both sides by {factor} to clear the fractions.",
  clearDecimals: "Multiply both sides by {factor} to clear the decimals.",
  addBoth: "Add {term} to both sides.",
  subtractBoth: "Subtract {term} from both sides.",
  divideBoth: "Divide both sides by {divisor}.",
  checkHeading: "Check the answer",
  checkIntro: "Substitute {variable} = {value} into each side:",
  checkLeft: "Left side: {working} = {value}",
  checkRight: "Right side: {working} = {value}",
  checkMatch: "Both sides equal {value}, so {variable} = {answer} is correct.",
  noSolutionTitle: "Why there is no solution",
  noSolution:
    "The terms in {variable} cancel and what is left, {statement}, is false. No value of {variable} can make it true, so the equation has no solution. On a graph, the two sides are parallel lines that never meet.",
  allSolutionsTitle: "Why every number works",
  allSolutions:
    "The terms in {variable} cancel and what is left, {statement}, is always true. Every value of {variable} is a solution — the two sides are the same line.",
  nonlinear:
    "After moving everything to one side, the highest power of {variable} is {degree}, so this is not a linear equation. A degree-2 equation is a quadratic: use the Quadratic Equation Solver. For higher degrees, the Polynomial Calculator finds the roots numerically.",
  graphHeading: "Graph of each side",
  graphTitle:
    "Graphs of y = {left} and y = {right}; they cross where the equation holds",
  point: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  enter: "Enter an equation to solve it.",
  errors: {
    empty: "Enter an equation to solve it.",
    "too-long": "That equation is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not allowed. Use digits, one letter for the unknown, + − × / ^ and brackets.",
    unexpected:
      "Something is out of place near “{token}”. Check for a missing operator or an extra symbol.",
    end: "The equation ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match. Check each ( has a ).",
    "divide-by-variable":
      "Dividing by a variable gives an equation that is not linear. Divide only by numbers, such as x/3.",
    "divide-by-zero": "Division by zero is not allowed.",
    exponent:
      "Powers must be whole numbers, such as x^2. Fractional powers are not supported.",
    "too-large": "That expression is too large to expand. Simplify it first.",
    "no-relation": "An equation needs an equals sign, such as 2x + 3 = 11.",
    "many-relations": "Use exactly one equals sign.",
    "many-variables":
      "More than one letter appears ({token}). This tool solves for one unknown — use the System of Equations Solver for two or three.",
    "missing-side": "Both sides of the equals sign need something on them.",
  },
} as const;

type Strings = typeof LINEAR_EQUATION_SOLVER_STRINGS;

const DECIMAL_INPUT = /[.,]/;

function termLabel(
  amount: Rational,
  degree: number,
  variable: string,
  show: (value: Rational) => string
): string {
  const size = absR(amount);
  const isOne = size.num === BigInt(1) && size.den === BigInt(1);
  const body = isOne && degree > 0 ? "" : show(size);
  const wrapped = body.includes("/") && degree > 0 ? `(${body})` : body;
  return `${wrapped}${variableText(variable, degree)}`;
}

function describeStep(
  step: RelationStep,
  strings: Strings,
  show: (value: Rational) => string,
  variable: string,
  style: NumberStyle
): string {
  const amount = step.amount;
  switch (step.kind) {
    case "clear":
      return t(
        style === "decimal" ? strings.clearDecimals : strings.clearFractions,
        {
          factor: amount ? show(amount) : "",
        }
      );
    case "move-variable":
    case "move-constant": {
      if (!amount) {
        return "";
      }
      const term = termLabel(amount, step.degree ?? 0, variable, show);
      return t(signR(amount) > 0 ? strings.subtractBoth : strings.addBoth, {
        term,
      });
    }
    case "divide":
      return t(strings.divideBoth, { divisor: amount ? show(amount) : "" });
    default:
      return strings.simplify;
  }
}

function Graph({
  left,
  right,
  value,
  variable,
  strings,
  style,
}: {
  left: Uni;
  right: Uni;
  value: Rational | null;
  variable: string;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const at = (poly: Uni) => (x: number) =>
    poly.reduceRight((sum, c) => sum * x + toNumberR(c), 0);
  const center = value ? toNumberR(value) : 0;
  const fns = [at(left), at(right)];
  const leftText = formatUni(left, variable, style, mark);
  const rightText = formatUni(right, variable, style, mark);
  const label = (x: number, y: number) =>
    t(strings.point, {
      x: fmt(x, { maximumFractionDigits: 3 }),
      y: fmt(y, { maximumFractionDigits: 3 }),
    });
  const marks = value
    ? [
        {
          x: center,
          y: toNumberR(uniEval(left, value)),
          label: label(center, toNumberR(uniEval(left, value))),
        },
      ]
    : [];
  return (
    <LinesGraph
      downloadLabel={strings.downloadPng}
      fileName="linear-equation-graph.png"
      lines={[
        { fn: fns[0], label: `y = ${leftText}` },
        { fn: fns[1], label: `y = ${rightText}` },
      ]}
      marks={marks}
      title={t(strings.graphTitle, { left: leftText, right: rightText })}
      viewport={fitWindow(
        center,
        fns,
        marks.map((m) => m.y)
      )}
    />
  );
}

function stepRows(
  reading: Extract<ReturnType<typeof solveLinearText>, { ok: true }>,
  typed: string,
  strings: Strings,
  style: NumberStyle,
  mark: string
): WorkedStep[] {
  const { variable, solution } = reading;
  const show = (value: Rational) => formatStyled(value, style, mark);
  const squash = (text: string) => text.replace(/\s+/g, "").replace(/-/g, "−");
  const statement = (step: RelationStep) =>
    step.parts.map((p) => formatUni(p, variable, style, mark)).join(" = ");
  const rows: WorkedStep[] = [{ text: strings.start, math: typed.trim() }];
  for (const step of solution.steps) {
    const math = statement(step);
    if (step.kind === "simplify" && squash(math) === squash(typed)) {
      continue;
    }
    rows.push({
      text: describeStep(step, strings, show, variable, style),
      math,
    });
  }
  return rows;
}

function Check({
  reading,
  strings,
  style,
  mark,
}: {
  reading: Extract<ReturnType<typeof solveLinearText>, { ok: true }>;
  strings: Strings;
  style: NumberStyle;
  mark: string;
}) {
  const { solution, variable, left, right } = reading;
  const value = solution.value;
  if (!value) {
    return null;
  }
  const show = (v: Rational) => formatStyled(v, style, mark);
  const both = checkAt(left, right, value);
  return (
    <div className="space-y-1 text-sm">
      <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
      <p className="text-muted-foreground">
        {t(strings.checkIntro, { variable, value: show(value) })}
      </p>
      <p className="font-mono text-foreground">
        {t(strings.checkLeft, {
          working: substitutionText(left, value, style, mark),
          value: show(both.left),
        })}
      </p>
      <p className="font-mono text-foreground">
        {t(strings.checkRight, {
          working: substitutionText(right, value, style, mark),
          value: show(both.right),
        })}
      </p>
      <p className="text-muted-foreground">
        {t(strings.checkMatch, {
          value: show(both.left),
          variable,
          answer: show(value),
        })}
      </p>
    </div>
  );
}

function kindOf(kind: string, strings: Strings): string {
  if (kind === "solved") {
    return strings.kinds.one;
  }
  return kind === "identity" ? strings.kinds.all : strings.kinds.none;
}

function Results({ typed, strings }: { typed: string; strings: Strings }) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const reading = solveLinearText(typed);
  if (!reading.ok) {
    const template = strings.errors[reading.error.code] ?? strings.errors.empty;
    return (
      <ErrorNotice>{t(template, { token: reading.error.token })}</ErrorNotice>
    );
  }
  const style: NumberStyle = DECIMAL_INPUT.test(typed) ? "decimal" : "fraction";
  const { solution, variable } = reading;
  const outcome = solution.outcome;
  const show = (value: Rational) => formatStyled(value, style, mark);
  const last = solution.steps.at(-1);
  const statement = last
    ? last.parts.map((p) => formatUni(p, variable, style, mark)).join(" = ")
    : "";
  const answer = solution.value ? `${variable} = ${show(solution.value)}` : "";
  const nonTerminating = solution.value && solution.value.den !== BigInt(1);
  const solvable = outcome.kind === "solved";
  return (
    <>
      {outcome.kind === "nonlinear" ? (
        <Notice>
          {t(strings.nonlinear, { variable, degree: outcome.degree })}
        </Notice>
      ) : (
        <StatGrid>
          <StatCard
            emphasis
            label={strings.answer}
            value={answer || (outcome.kind === "identity" ? "ℝ" : "∅")}
          />
          {nonTerminating && solution.value ? (
            <StatCard
              label={strings.decimal}
              value={fmt(toNumberR(solution.value), {
                maximumFractionDigits: 6,
              })}
            />
          ) : null}
          <StatCard
            label={strings.type}
            value={kindOf(outcome.kind, strings)}
          />
        </StatGrid>
      )}
      {solvable && answer ? (
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={answer}
        />
      ) : null}
      {outcome.kind === "nonlinear" ? null : (
        <>
          <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
          <StepList steps={stepRows(reading, typed, strings, style, mark)} />
        </>
      )}
      {outcome.kind === "contradiction" ? (
        <div className={RESULT_CARD}>
          <p className="font-medium text-foreground text-sm">
            {strings.noSolutionTitle}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {t(strings.noSolution, { variable, statement })}
          </p>
        </div>
      ) : null}
      {outcome.kind === "identity" ? (
        <div className={RESULT_CARD}>
          <p className="font-medium text-foreground text-sm">
            {strings.allSolutionsTitle}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {t(strings.allSolutions, { variable, statement })}
          </p>
        </div>
      ) : null}
      {solvable ? (
        <Check mark={mark} reading={reading} strings={strings} style={style} />
      ) : null}
      {outcome.kind === "nonlinear" ? null : (
        <>
          <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
          <Graph
            left={reading.left}
            right={reading.right}
            strings={strings}
            style={style}
            value={solution.value}
            variable={variable}
          />
        </>
      )}
    </>
  );
}

export default function LinearEquationSolverWidget() {
  const strings = useToolUi(LINEAR_EQUATION_SOLVER_STRINGS);
  const [equation, setEquation] = useState("3(x − 2) + 4 = 2x + 7");
  return (
    <>
      <TextField
        hint={strings.equationHint}
        id="les-equation"
        label={strings.equation}
        mono
        onChange={setEquation}
        value={equation}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.examples.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setEquation(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {equation.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results strings={strings} typed={equation} />
        )}
      </section>
    </>
  );
}
